import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Wrench, Camera, VideoCamera, FilePdf, Package, MapPin, Spinner,
  ArrowRight, MagnifyingGlass, CheckCircle, XCircle, Receipt, Trash, Image,
  CalendarBlank, ClockCounterClockwise, Buildings, HardDrives, Plus,
  Sparkle, Warning, ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  default as api,
  fetchServiceTechFleet, fetchServiceTechOpenQuote, createServiceVisit,
  uploadServiceVisitMedia, finalizeServiceVisit, listServiceVisits,
  previewServiceVisitSn,
} from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import LiveCameraCapture from "@/components/LiveCameraCapture";
import { useT } from "@/lib/i18n";

/**
 * ServiceTechPage — on-site capture wizard for service-tech visits.
 *
 * Session 2 (this file) ships the capture wizard end-to-end with mock-AI
 * stubs on the backend. Session 3 will replace the stub `ai_result` with
 * Claude-Sonnet-4.5 vision OCR + auto-allocation of `Quote_Detail` lines
 * to the compressor's `Related to Product SN`.
 *
 * Flow:
 *   1. PICK     — search / list compressors visible to caller
 *   2. PREFLIGHT — verify open quote exists (block if none)
 *   3. CAPTURE  — 4 sub-steps: nameplate / invoice / parts / video
 *   4. REVIEW   — confirm + submit
 *   5. DONE     — success card with visit id + AI placeholder
 */

const STEPS = ["pick", "preflight", "capture", "review", "done"];

// Each capture step records a single file. We blow them out into 4 visual
// cards so the tech can see at a glance what's done.
const CAPTURE_KINDS = [
  { kind: "nameplate", label: "Compressor nameplate", desc: "Photo of the metal nameplate where the serial number is registered.", icon: Camera, accept: "image/*", isVideo: false, tone: "blue", required: true },
  { kind: "invoice",   label: "Customer invoice",     desc: "Photo of the invoice the customer received. AI will decode the line items.", icon: Receipt, accept: "image/*", isVideo: false, tone: "emerald", required: true },
  { kind: "parts",     label: "Parts received",       desc: "Photo of the actual parts you'll be installing — record-only audit trail.", icon: Package, accept: "image/*", isVideo: false, tone: "violet", required: true },
  { kind: "video",     label: "Before & after video", desc: "Short clip of the compressor running before and after service.", icon: VideoCamera, accept: "video/*", isVideo: true, tone: "amber", required: true },
];

export default function ServiceTechPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [gate, setGate] = useState({ can: null, as: null, reason: null });
  const [gateLoading, setGateLoading] = useState(true);
  const [step, setStep] = useState("pick");

  // pick
  const [pickQuery, setPickQuery] = useState("");
  const [fleet, setFleet] = useState({ items: [], count: 0, loading: false });
  const [recent, setRecent] = useState([]);
  const [selected, setSelected] = useState(null);    // chosen compressor row

  // preflight
  const [preflight, setPreflight] = useState({ status: "idle", data: null, error: null });

  // capture
  const [visit, setVisit] = useState(null);          // server-issued visit doc
  const [captures, setCaptures] = useState({});      // { kind: { file, previewUrl, progress, media, error } }
  const [gps, setGps] = useState(null);
  const [snPreview, setSnPreview] = useState(null);  // {sn_match, extracted_sn, confidence, loading?}

  // review / done
  const [submitting, setSubmitting] = useState(false);
  const [finalVisit, setFinalVisit] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitErrorDetail, setSubmitErrorDetail] = useState(null);

  // ─── Gate check ───
  useEffect(() => {
    let cancelled = false;
    api.get("/me/can-service-tech")
      .then((r) => { if (!cancelled) setGate(r.data); })
      .catch(() => { if (!cancelled) setGate({ can: false, reason: "Could not check permission" }); })
      .finally(() => { if (!cancelled) setGateLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ─── Recent visits ───
  const reloadRecent = useCallback(async () => {
    try {
      const res = await listServiceVisits(10);
      setRecent(res.items || []);
    } catch { /* non-blocking */ }
  }, []);
  useEffect(() => { if (gate?.can) reloadRecent(); }, [gate?.can, reloadRecent]);

  // ─── Fleet search (debounced) ───
  useEffect(() => {
    if (!gate?.can) return;
    let cancelled = false;
    setFleet((f) => ({ ...f, loading: true }));
    const t = setTimeout(() => {
      fetchServiceTechFleet(pickQuery, 60)
        .then((res) => { if (!cancelled) setFleet({ items: res.items || [], count: res.count || 0, loading: false }); })
        .catch(() => { if (!cancelled) setFleet({ items: [], count: 0, loading: false }); });
    }, pickQuery ? 280 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pickQuery, gate?.can]);

  // ─── GPS (best-effort silent capture) ───
  const requestGps = useCallback(() => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve(null),
      { maximumAge: 60_000, timeout: 4_000, enableHighAccuracy: false },
    );
  }), []);

  // ─── Step transitions ───
  const handlePick = useCallback(async (row) => {
    setSelected(row);
    setStep("preflight");
    setPreflight({ status: "loading", data: null, error: null });
    // fire GPS in parallel — best effort
    requestGps().then(setGps);
    try {
      const data = await fetchServiceTechOpenQuote(row.serial_id);
      setPreflight({ status: "ok", data, error: null });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message) || (typeof detail === "string" && detail) || "No open quote found.";
      const code = (typeof detail === "object" && detail?.code) || "ERROR";
      setPreflight({ status: "error", data: null, error: { code, message: msg } });
    }
  }, [requestGps]);

  const handleStartCapture = useCallback(async () => {
    if (!selected || !preflight.data) return;
    try {
      const v = await createServiceVisit({
        serial_id: selected.serial_id,
        quote_id: preflight.data.quote.quote_id,
        gps,
        started_at: new Date().toISOString(),
      });
      setVisit(v);
      setCaptures({});
      setStep("capture");
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || err?.message || "Could not start visit");
    }
  }, [selected, preflight.data, gps]);

  const handleCaptureFile = useCallback(async (kindObj, file) => {
    if (!visit || !file) return;
    const k = kindObj.kind;
    const previewUrl = (kindObj.isVideo ? null : URL.createObjectURL(file));
    setCaptures((c) => ({ ...c, [k]: { file, previewUrl, progress: 0, media: null, error: null, name: file.name, size: file.size } }));
    try {
      const media = await uploadServiceVisitMedia(visit.id, k, file, {
        onProgress: (pct) => setCaptures((c) => ({ ...c, [k]: { ...(c[k] || {}), progress: pct } })),
      });
      setCaptures((c) => ({ ...c, [k]: { ...(c[k] || {}), progress: 100, media } }));
      // P3: Live SN preview — fire as soon as the nameplate uploads, advisory only.
      if (k === "nameplate") {
        setSnPreview({ loading: true });
        previewServiceVisitSn(visit.id)
          .then((res) => setSnPreview({ ...res, loading: false }))
          .catch(() => setSnPreview(null));
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      setCaptures((c) => ({ ...c, [k]: { ...(c[k] || {}), error: typeof msg === "string" ? msg : JSON.stringify(msg) } }));
    }
  }, [visit]);

  const handleRetake = useCallback((k) => {
    setCaptures((c) => {
      const next = { ...c };
      if (next[k]?.previewUrl) URL.revokeObjectURL(next[k].previewUrl);
      delete next[k];
      return next;
    });
    if (k === "nameplate") setSnPreview(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!visit) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitErrorDetail(null);
    try {
      const fin = await finalizeServiceVisit(visit.id, {
        finished_at: new Date().toISOString(),
        notes: "",
      });
      setFinalVisit(fin);
      setStep("done");
      reloadRecent();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        setSubmitError(detail.message || "Could not submit visit");
        setSubmitErrorDetail(detail);
      } else {
        const msg = (typeof detail === "string" && detail) || err?.message || "Could not submit visit";
        setSubmitError(msg);
        setSubmitErrorDetail(null);
      }
    } finally {
      setSubmitting(false);
    }
  }, [visit, reloadRecent]);

  const handleStartOver = useCallback(() => {
    // Cleanup blob URLs
    Object.values(captures).forEach((c) => { if (c?.previewUrl) URL.revokeObjectURL(c.previewUrl); });
    setSelected(null); setVisit(null); setCaptures({}); setPreflight({ status: "idle" });
    setFinalVisit(null); setSubmitError(null); setStep("pick");
  }, [captures]);

  const allCaptured = useMemo(
    () => CAPTURE_KINDS.every((c) => captures[c.kind]?.media?.id),
    [captures],
  );

  // ─── Loading / gate UI ───
  if (gateLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Spinner size={28} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (!gate.can) {
    return (
      <div className="min-h-screen bg-white" data-testid="svc-tech-gate-blocked">
        <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-5xl mx-auto">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" title={t("action.back", "Back")} data-testid="svc-tech-back">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <Wrench size={20} weight="fill" className="text-slate-300" />
          <h1 className="text-base font-black tracking-tighter font-['Chivo']">{t("service_tech.page_header_short")}</h1>
          <div className="ml-auto"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
        </header>
        <main className="max-w-3xl mx-auto p-8 text-center">
          <XCircle size={56} weight="duotone" className="mx-auto text-rose-300 mb-3" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">{t("service_tech.gate_blocked_title")}</h2>
          <p className="text-sm text-slate-500">
            {t("service_tech.gate_blocked_body")}
            <br /><br />
            <span className="text-[11px] font-mono text-slate-400">{gate.reason || "no compressor ownership"}</span>
          </p>
        </main>
      </div>
    );
  }

  // ─── Main wizard ───
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="svc-tech-page">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-3 md:px-8 h-14 flex items-center gap-2 md:gap-3 min-w-0">
          <button onClick={() => navigate("/")} className="shrink-0 w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" title={t("action.back", "Back")} data-testid="svc-tech-back">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <Wrench size={20} weight="fill" className="text-blue-700 shrink-0 hidden xs:inline-block" />
          <h1 className="text-sm sm:text-base md:text-lg font-black tracking-tighter font-['Chivo'] truncate min-w-0">
            <span className="hidden sm:inline">{t("service_tech.page_header_full")}</span>
            <span className="sm:hidden">{t("service_tech.page_header_short")}</span>
          </h1>
          <span className="shrink-0 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 border border-violet-200 px-1.5 py-0.5 rounded-sm" data-testid="svc-tech-role">
            {gate.as === "staff" ? "Staff" : "Owner"}
          </span>
          <div className="ml-auto shrink-0"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
        </div>
        <ProgressBar step={step} />
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-8">
        {step === "pick" && (
          <PickStep
            query={pickQuery} setQuery={setPickQuery}
            fleet={fleet} onPick={handlePick}
            recent={recent}
          />
        )}
        {step === "preflight" && (
          <PreflightStep
            compressor={selected} state={preflight}
            onBack={() => setStep("pick")}
            onContinue={handleStartCapture}
          />
        )}
        {step === "capture" && (
          <CaptureStep
            visit={visit} compressor={selected} preflight={preflight.data}
            captures={captures} onCapture={handleCaptureFile} onRetake={handleRetake}
            onContinue={() => setStep("review")} canContinue={allCaptured}
            submitError={submitError}
            snPreview={snPreview}
          />
        )}
        {step === "review" && (
          <ReviewStep
            visit={visit} compressor={selected} preflight={preflight.data}
            captures={captures} gps={gps}
            onBack={() => setStep("capture")}
            onSubmit={handleSubmit} submitting={submitting}
            submitError={submitError} submitErrorDetail={submitErrorDetail}
          />
        )}
        {step === "done" && (
          <DoneStep
            visit={finalVisit} compressor={selected} preflight={preflight.data}
            onStartOver={handleStartOver}
            onHome={() => navigate("/")}
          />
        )}
      </main>
    </div>
  );
}

// ─── Progress bar ───
function ProgressBar({ step }) {
  const t = useT();
  const idx = STEPS.indexOf(step);
  const labels = [
    t("svc_tech.step_compressor", "Compressor"),
    t("svc_tech.step_open_quote", "Open quote"),
    t("svc_tech.step_capture", "Capture"),
    t("svc_tech.step_review", "Review"),
    t("svc_tech.step_done", "Done"),
  ];
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 pb-2">
      <div className="flex items-center gap-1.5">
        {labels.map((lbl, i) => (
          <div key={lbl} className="flex items-center gap-1.5 flex-1 last:flex-none">
            <div
              className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                i < idx ? "bg-emerald-600 text-white" :
                i === idx ? "bg-blue-700 text-white ring-4 ring-blue-100" :
                "bg-slate-200 text-slate-500"
              }`}
              data-testid={`svc-tech-step-${i}`}
              data-step-active={i === idx ? "true" : "false"}
              data-step-done={i < idx ? "true" : "false"}
            >
              {i < idx ? <CheckCircle size={10} weight="fill" /> : i + 1}
            </div>
            <span className={`text-[10px] uppercase tracking-wider font-bold ${i === idx ? "text-blue-700" : i < idx ? "text-emerald-700" : "text-slate-400"} hidden sm:inline`}>{lbl}</span>
            {i < labels.length - 1 && <div className={`flex-1 h-px ${i < idx ? "bg-emerald-300" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Step 1 — Pick compressor ───
function PickStep({ query, setQuery, fleet, onPick, recent }) {
  const t = useT();
  return (
    <div data-testid="svc-tech-step-pick">
      <h2 className="text-2xl font-black tracking-tight mb-1 font-['Chivo']">{t("svc_tech.pick_title", "Pick the compressor")}</h2>
      <p className="text-sm text-slate-500 mb-5">{t("svc_tech.pick_hint", "Search by serial number, product code, or company name. The list shows compressors you're authorized to service.")}</p>

      <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
        <div className="border-b border-slate-200 px-3 py-2 flex items-center gap-2">
          <MagnifyingGlass size={14} className="text-slate-400" />
          <input
            type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={t("svc_tech.search_placeholder", "Search by serial, product, or company…")}
            className="flex-1 outline-none text-sm placeholder:text-slate-400"
            data-testid="svc-tech-fleet-search" autoFocus
          />
          {fleet.loading && <Spinner size={14} className="animate-spin text-slate-400" />}
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="svc-tech-fleet-list">
          {fleet.items.length === 0 && !fleet.loading && (
            <div className="px-4 py-12 text-center text-sm text-slate-400">
              {query ? t("svc_tech.no_match", "No compressors match that search.") : t("svc_tech.no_visible", "No compressors visible.")}
            </div>
          )}
          {fleet.items.map((row, i) => (
            <button
              key={row.serial_id}
              onClick={() => onPick(row)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-blue-50/50 transition-colors flex items-start gap-3 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"}`}
              data-testid={`svc-tech-pick-${row.serial_id}`}
            >
              <HardDrives size={18} weight="duotone" className="text-blue-700 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-bold text-slate-800">SN {row.serial_number || "—"}</span>
                  <span className="text-xs text-slate-500">·</span>
                  <span className="text-sm text-slate-700">{row.product_name || t("svc_tech.unknown_product", "Unknown product")}</span>
                </div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                  <Buildings size={11} weight="fill" /> {row.company_name || row.customer_id || "—"}
                  {row.hours != null && <span className="ml-2 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1 py-0.5 rounded-sm">{Math.round(row.hours)} h</span>}
                </div>
              </div>
              <ArrowRight size={14} className="text-slate-300 mt-1.5" />
            </button>
          ))}
        </div>
      </div>

      {recent.length > 0 && (
        <div className="mt-8">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center gap-1.5">
            <ClockCounterClockwise size={12} weight="fill" /> {t("svc_tech.recent_visits", "Recent visits")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {recent.slice(0, 6).map((v) => (
              <div key={v.id} className="px-3 py-2 bg-white rounded-md border border-slate-200 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-bold uppercase px-1 py-0.5 rounded-sm ${v.status === "submitted" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{v.status}</span>
                  <span className="font-bold text-slate-700">SN {v.serial_number || "—"}</span>
                  <span className="text-slate-400 ml-auto">{relTime(v.started_at)}</span>
                </div>
                <div className="text-slate-500 truncate">{v.product_name} · {v.company_name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 2 — Pre-flight (open quote check) ───
function PreflightStep({ compressor, state, onBack, onContinue }) {
  const t = useT();
  return (
    <div data-testid="svc-tech-step-preflight">
      <h2 className="text-2xl font-black tracking-tight mb-1 font-['Chivo']">{t("svc_tech.preflight_title", "Open quote check")}</h2>
      <p className="text-sm text-slate-500 mb-5">{t("svc_tech.preflight_hint", "Service techs need an open quote with parts already purchased. We'll match what you used against this quote.")}</p>

      <CompressorBadge row={compressor} />

      {state.status === "loading" && (
        <div className="bg-white rounded-md border border-slate-200 p-8 text-center mt-4">
          <Spinner size={28} className="animate-spin text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-500">{t("svc_tech.looking_up_quotes", "Looking up open quotes for")} {compressor?.company_name || "this customer"}…</p>
        </div>
      )}

      {state.status === "error" && (
        <div className="bg-rose-50 border border-rose-200 rounded-md p-5 mt-4" data-testid="svc-tech-preflight-blocked">
          <div className="flex items-start gap-3">
            <XCircle size={28} weight="duotone" className="text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-bold text-rose-900 mb-1">{t("svc_tech.cannot_start", "Cannot start service")}</div>
              <p className="text-sm text-rose-800 leading-relaxed">{state.error?.message}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={onBack} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-white border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-100" data-testid="svc-tech-preflight-back">
                  {t("svc_tech.pick_another", "Pick another compressor")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {state.status === "ok" && (
        <div className="mt-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 mb-4 flex items-start gap-3" data-testid="svc-tech-preflight-ok">
            <CheckCircle size={24} weight="fill" className="text-emerald-600 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-bold text-emerald-900">{t("svc_tech.open_quote_found", "Open quote found · ready to start")}</div>
              <div className="text-xs text-emerald-800 mt-0.5">{t("svc_tech.will_allocate", "We'll allocate the parts you use against this quote.")}</div>
            </div>
          </div>
          <QuoteSummaryCard quote={state.data?.quote} lines={state.data?.lines || []} />
          <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-between">
            <button onClick={onBack} className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded-sm hover:bg-slate-50">
              {t("action.back", "Back")}
            </button>
            <button
              onClick={onContinue}
              className="px-5 py-2.5 text-sm font-bold uppercase tracking-wider bg-blue-700 text-white rounded-sm hover:bg-blue-800 inline-flex items-center justify-center gap-2"
              data-testid="svc-tech-preflight-continue"
            >
              {t("svc_tech.start_capture", "Start capture")} <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CompressorBadge({ row }) {
  if (!row) return null;
  return (
    <div className="bg-white rounded-md border border-slate-200 p-3 flex items-center gap-3" data-testid="svc-tech-comp-badge">
      <div className="w-10 h-10 rounded-md bg-blue-700 text-white flex items-center justify-center">
        <HardDrives size={20} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-800 truncate">SN {row.serial_number} · {row.product_name}</div>
        <div className="text-xs text-slate-500 truncate flex items-center gap-1.5"><Buildings size={11} weight="fill" />{row.company_name || row.customer_id}</div>
      </div>
    </div>
  );
}

function QuoteSummaryCard({ quote, lines }) {
  const t = useT();
  if (!quote) return null;
  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
        <FilePdf size={16} weight="fill" className="text-slate-500" />
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-800">Quote {quote.quote_id}</div>
          <div className="text-[11px] text-slate-500 flex items-center gap-2">
            <CalendarBlank size={10} weight="fill" /> {quote.quote_date || "—"}
            {quote.po_number && <><span>·</span><span>PO {quote.po_number}</span></>}
            <span>·</span><span className="font-bold text-emerald-700 uppercase tracking-wider text-[10px]">{quote.status || "Quote"}</span>
          </div>
        </div>
      </div>
      <div className="px-4 py-2 max-h-56 overflow-y-auto">
        {lines.length === 0 && <div className="text-xs text-slate-400 py-3 text-center">{t("svc_tech.no_line_items", "No line items on this quote yet.")}</div>}
        {lines.map((ln, i) => (
          <div key={ln.detail_id} className={`flex items-center gap-3 py-1.5 text-xs ${i > 0 ? "border-t border-slate-100" : ""}`}>
            <span className="text-slate-300 w-5 text-right">{i + 1}.</span>
            <span className="flex-1 text-slate-700 truncate">{ln.product_name}</span>
            <span className="text-slate-500">×{Number(ln.qty || 0)}</span>
            {ln.related_serial && <span className="text-[10px] uppercase tracking-wider bg-violet-100 text-violet-700 px-1 py-0.5 rounded-sm">SN {ln.related_serial}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Step 3 — Capture ───
function CaptureStep({ visit, compressor, preflight, captures, onCapture, onRetake, onContinue, canContinue, submitError, snPreview }) {
  const t = useT();
  return (
    <div data-testid="svc-tech-step-capture">
      <h2 className="text-2xl font-black tracking-tight mb-1 font-['Chivo']">{t("svc_tech.capture_title", "Capture the visit")}</h2>
      <p className="text-sm text-slate-500 mb-4">{t("svc_tech.capture_hint", "Tap each card to take the photo or video. All four are required before submission.")}</p>
      <CompressorBadge row={compressor} />
      {visit?.id && <div className="text-[10px] font-mono text-slate-400 mt-1 mb-4" data-testid="svc-tech-visit-id">visit_id: {visit.id}</div>}

      {snPreview && <SnPreviewBanner snPreview={snPreview} expectedSn={compressor?.serial_number} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {CAPTURE_KINDS.map((c) => (
          <CaptureCard
            key={c.kind} kindObj={c}
            state={captures[c.kind]}
            onPick={(file) => onCapture(c, file)}
            onRetake={() => onRetake(c.kind)}
          />
        ))}
      </div>

      {submitError && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-md px-3 py-2 mb-3">{String(submitError)}</div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
        <button
          onClick={onContinue}
          disabled={!canContinue}
          className="px-5 py-2.5 text-sm font-bold uppercase tracking-wider bg-blue-700 text-white rounded-sm hover:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          data-testid="svc-tech-capture-continue"
        >
          {canContinue ? "Continue to review" : "Capture all 4 to continue"} <ArrowRight size={14} weight="bold" />
        </button>
      </div>

      <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
        <MapPin size={12} weight="fill" />
        GPS, server timestamps, and your start/end times are recorded automatically.
      </div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-violet-600">
        <Sparkle size={12} weight="fill" />
        Submission triggers Claude Sonnet 4.5 vision — takes ~10–15 seconds.
      </div>
    </div>
  );
}

function CaptureCard({ kindObj, state, onPick, onRetake }) {
  const [camOpen, setCamOpen] = useState(false);
  const tone = {
    blue:    { ring: "border-blue-200",    iconBg: "bg-blue-600",    label: "text-blue-700"   },
    emerald: { ring: "border-emerald-200", iconBg: "bg-emerald-600", label: "text-emerald-700"},
    violet:  { ring: "border-violet-200",  iconBg: "bg-violet-600",  label: "text-violet-700" },
    amber:   { ring: "border-amber-200",   iconBg: "bg-amber-600",   label: "text-amber-800"  },
  }[kindObj.tone];
  const Icon = kindObj.icon;
  const done = !!state?.media?.id;
  const uploading = state && !done && !state.error && state.progress != null && state.progress < 100;
  return (
    <div className={`p-3 rounded-md border ${tone.ring} bg-white shadow-sm`} data-testid={`svc-tech-capture-${kindObj.kind}`} data-capture-done={done ? "true" : "false"}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-9 h-9 rounded-md ${tone.iconBg} text-white flex items-center justify-center`}>
          <Icon size={18} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[11px] font-bold uppercase tracking-wider ${tone.label}`}>{kindObj.label}</div>
          <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{kindObj.desc}</div>
        </div>
        {done && <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />}
      </div>

      {/* iter137.5 — strict live capture; opens the in-app camera. */}
      <LiveCameraCapture
        open={camOpen}
        mode={kindObj.isVideo ? "video" : "photo"}
        maxSeconds={120}
        onClose={() => setCamOpen(false)}
        onCapture={(blob, info) => {
          const f = new File([blob], info.filename, { type: info.mime_type });
          onPick(f);
        }}
        testIdPrefix={`svc-tech-livecam-${kindObj.kind}`}
      />

      {!state && (
        <button
          onClick={() => setCamOpen(true)}
          className="mt-3 w-full px-3 py-2.5 text-xs font-bold uppercase tracking-wider bg-slate-900 text-white rounded-sm hover:bg-slate-800 inline-flex items-center justify-center gap-2"
          data-testid={`svc-tech-tap-${kindObj.kind}`}
        >
          <Camera size={14} weight="fill" /> Tap to capture (live only)
        </button>
      )}

      {state && (
        <div className="mt-3">
          {state.previewUrl && !kindObj.isVideo && (
            <div className="relative rounded-sm overflow-hidden bg-slate-100 mb-2 aspect-video">
              <img src={state.previewUrl} alt={`${kindObj.label} preview`} className="w-full h-full object-contain" />
            </div>
          )}
          {kindObj.isVideo && state.file && (
            <div className="bg-slate-900 text-white rounded-sm p-3 mb-2 flex items-center gap-2 text-xs">
              <VideoCamera size={16} weight="fill" className="text-amber-300" />
              <span className="truncate flex-1">{state.name}</span>
              <span className="text-slate-300">{prettyBytes(state.size)}</span>
            </div>
          )}
          {uploading && (
            <div className="mb-2">
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 transition-all" style={{ width: `${state.progress || 0}%` }} />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Uploading… {state.progress || 0}%</div>
            </div>
          )}
          {state.error && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-sm mb-2">{state.error}</div>
          )}
          <div className="flex gap-2">
            <button
              onClick={onRetake}
              className="flex-1 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded-sm hover:bg-slate-50 inline-flex items-center justify-center gap-1"
              data-testid={`svc-tech-retake-${kindObj.kind}`}
            >
              <Trash size={11} /> Retake
            </button>
            {done && (
              <span className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded-sm inline-flex items-center gap-1">
                <CheckCircle size={11} weight="fill" /> Saved
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SnPreviewBanner({ snPreview, expectedSn }) {
  const t = useT();
  if (snPreview.loading) {
    return (
      <div className="bg-violet-50 border border-violet-200 rounded-md p-3 mb-3 flex items-center gap-2 text-xs" data-testid="svc-tech-sn-preview-loading">
        <Spinner size={14} className="animate-spin text-violet-600" />
        <span className="font-bold text-violet-800">{t("svc_tech.reading_nameplate", "Reading nameplate…")}</span>
        <span className="text-violet-700">{t("svc_tech.ai_extracting_sn", "AI is extracting the serial number from your photo.")}</span>
      </div>
    );
  }
  const ok = snPreview.sn_match;
  const tone = ok ? "emerald" : "rose";
  const Icon = ok ? CheckCircle : Warning;
  const cls = ok
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : "bg-rose-50 border-rose-200 text-rose-900";
  return (
    <div className={`rounded-md border p-3 mb-3 flex items-start gap-2 text-xs ${cls}`} data-testid="svc-tech-sn-preview">
      <Icon size={16} weight="fill" className={`shrink-0 mt-0.5 ${ok ? "text-emerald-600" : "text-rose-600"}`} />
      <div className="flex-1">
        <div className="font-bold mb-0.5">
          {ok
            ? `SN matches · ${Math.round((snPreview.confidence || 0) * 100)}% confidence`
            : "SN mismatch — wrong unit?"}
        </div>
        <div className={ok ? "text-emerald-800" : "text-rose-800"}>
          Read <strong className="font-mono">{snPreview.extracted_sn || "(unreadable)"}</strong> from the nameplate;
          this compressor's record SN is <strong className="font-mono">{expectedSn}</strong>.
          {!ok && " Re-shoot the nameplate or stop and verify the unit before continuing."}
        </div>
      </div>
    </div>
  );
}

// ─── Step 4 — Review & submit ───
function ReviewStep({ visit, compressor, preflight, captures, gps, onBack, onSubmit, submitting, submitError, submitErrorDetail }) {
  const t = useT();
  return (
    <div data-testid="svc-tech-step-review">
      <h2 className="text-2xl font-black tracking-tight mb-1 font-['Chivo']">Review & submit</h2>
      <p className="text-sm text-slate-500 mb-4">Last chance to retake anything. After submission the AI will read the nameplate and invoice and allocate parts.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Card title="Compressor">
          <div className="text-sm font-bold text-slate-800">SN {compressor?.serial_number}</div>
          <div className="text-xs text-slate-600">{compressor?.product_name}</div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Buildings size={11} weight="fill" />{compressor?.company_name}</div>
        </Card>
        <Card title="Open quote">
          <div className="text-sm font-bold text-slate-800">{preflight?.quote?.quote_id || "—"}</div>
          <div className="text-xs text-slate-600">{preflight?.quote?.quote_date} · {preflight?.lines?.length || 0} lines</div>
        </Card>
        <Card title="Visit">
          <div className="text-[10px] font-mono text-slate-500 truncate">{visit?.id}</div>
          <div className="text-xs text-slate-600 mt-1 flex items-center gap-1.5">
            <MapPin size={11} weight="fill" />
            {gps ? `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)} (±${Math.round(gps.accuracy || 0)}m)` : "GPS not available"}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {CAPTURE_KINDS.map((c) => (
          <ReviewThumb key={c.kind} kindObj={c} state={captures[c.kind]} />
        ))}
      </div>

      {submitErrorDetail?.code === "INSUFFICIENT_PARTS" && (
        <div className="bg-rose-50 border border-rose-200 rounded-md p-4 mb-3" data-testid="svc-tech-insufficient-parts">
          <div className="flex items-start gap-2 mb-2">
            <Warning size={20} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-rose-900">Not enough parts on the open quote</div>
              <div className="text-xs text-rose-800">{submitErrorDetail.message}</div>
            </div>
          </div>
          {Array.isArray(submitErrorDetail.insufficient) && submitErrorDetail.insufficient.length > 0 && (
            <div className="mt-2 bg-white rounded-sm border border-rose-200 overflow-hidden">
              <div className="text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 px-2 py-1">What's short</div>
              <ul className="px-3 py-2 text-xs space-y-1">
                {submitErrorDetail.insufficient.map((r, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="text-slate-700 truncate">{r.matched_description || r.detail_id}</span>
                    <span className="text-rose-700 shrink-0">need {r.wanted}, have {r.available ?? 0}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {submitErrorDetail?.code === "DB_WRITE_FAILED" && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-3" data-testid="svc-tech-db-error">
          <div className="flex items-start gap-2 mb-2">
            <Warning size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-amber-900">{t("svc_tech.db_write_blocked", "Database write blocked — try again shortly")}</div>
              <div className="text-xs text-amber-800">
                {t("svc_tech.db_write_blocked_detail1", "The AI read everything correctly and a draft allocation was prepared, but the database refused the write")}
                ({submitErrorDetail.message?.includes("1449") ? "stale trigger definer" : "permission issue"}{t("svc_tech.db_write_blocked_detail2", "). An admin needs to clear the trigger on the Bluehost side. Your photos and GPS are saved — tap Submit again once the DB is fixed.")}
              </div>
            </div>
          </div>
          {submitErrorDetail.vision?.invoice?.items_decoded?.length > 0 && (
            <div className="mt-2 bg-white rounded-sm border border-amber-200 overflow-hidden">
              <div className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-1">{t("svc_tech.ai_decoded", "AI decoded")}</div>
              <ul className="px-3 py-2 text-xs space-y-1">
                {submitErrorDetail.vision.invoice.items_decoded.map((r, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="text-slate-700 truncate">{r.description}</span>
                    <span className="text-slate-500 shrink-0">×{r.qty}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {submitError && !submitErrorDetail?.code && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-md px-3 py-2 mb-3" data-testid="svc-tech-submit-error">{String(submitError)}</div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
        <button onClick={onBack} disabled={submitting} className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded-sm hover:bg-slate-50">
          {t("svc_tech.back_to_capture", "Back to capture")}
        </button>
        <button
          onClick={onSubmit}
          disabled={submitting}
          className="px-5 py-2.5 text-sm font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:bg-slate-300 inline-flex items-center justify-center gap-2"
          data-testid="svc-tech-submit"
        >
          {submitting ? (
            <><Spinner size={14} className="animate-spin" /> Submitting…</>
          ) : submitErrorDetail?.code === "DB_WRITE_FAILED" ? (
            <><ArrowsClockwise size={14} weight="bold" /> Retry submit</>
          ) : (
            <><CheckCircle size={14} weight="fill" /> Submit visit</>
          )}
        </button>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-md border border-slate-200 p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{title}</div>
      {children}
    </div>
  );
}

function ReviewThumb({ kindObj, state }) {
  const Icon = kindObj.icon;
  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
      <div className="aspect-video bg-slate-100 flex items-center justify-center">
        {state?.previewUrl && !kindObj.isVideo ? (
          <img src={state.previewUrl} alt={kindObj.label} className="w-full h-full object-cover" />
        ) : kindObj.isVideo && state?.file ? (
          <VideoCamera size={28} className="text-slate-400" weight="fill" />
        ) : (
          <Image size={20} className="text-slate-300" />
        )}
      </div>
      <div className="px-2 py-1.5 text-[11px] flex items-center gap-1.5">
        <Icon size={12} weight="fill" className="text-slate-500" />
        <span className="truncate font-bold text-slate-700">{kindObj.label}</span>
        {state?.media?.id && <CheckCircle size={11} weight="fill" className="text-emerald-600 ml-auto" />}
      </div>
    </div>
  );
}

// ─── Step 5 — Done (with AI Vision results) ───
function DoneStep({ visit, compressor, preflight, onStartOver, onHome }) {
  const t = useT();
  const ai = visit?.ai_result || {};
  const snMatch = !!ai.sn_match;
  const snConfPct = Math.round((ai.sn_confidence || 0) * 100);
  const applied = Array.isArray(ai.applied) ? ai.applied : [];
  const unmatched = Array.isArray(ai.unmatched) ? ai.unmatched : [];
  const items = Array.isArray(ai.items_decoded) ? ai.items_decoded : [];
  const splits = applied.filter(a => a.split).length;
  return (
    <div className="max-w-2xl mx-auto" data-testid="svc-tech-step-done">
      <div className="bg-white rounded-md border border-emerald-200 shadow-sm p-6 md:p-8 text-center mb-4">
        <CheckCircle size={56} weight="duotone" className="text-emerald-600 mx-auto mb-3" />
        <h2 className="text-2xl font-black tracking-tight mb-1 font-['Chivo']">{t("svc_tech.visit_complete", "Visit complete")}</h2>
        <p className="text-sm text-slate-500 mb-3">
          {t("svc_tech.ai_allocated", "AI Vision analyzed the nameplate and invoice and allocated")}{" "}
          <strong className="text-slate-800">{applied.length} part{applied.length === 1 ? "" : "s"}</strong>
          {splits > 0 && <span> ({splits} line{splits === 1 ? "" : "s"} split)</span>} to compressor SN {compressor?.serial_number}.
        </p>
        <div className="text-[10px] font-mono text-slate-400">visit_id: {visit?.id}</div>
        {ai.fallback && (
          <div className="mt-3 inline-block text-[10px] font-bold uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-700 px-2 py-1 rounded-sm">
            AI mock mode — set SERVICE_TECH_AI_MOCK=0 for real vision
          </div>
        )}
      </div>

      {/* AI Vision · Nameplate */}
      <div className="bg-white rounded-md border border-slate-200 mb-3 overflow-hidden" data-testid="svc-tech-ai-nameplate">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-violet-600" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("svc_tech.ai_nameplate", "AI Vision · Nameplate")}</span>
          <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${snMatch ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {snMatch ? `Match · ${snConfPct}%` : "Mismatch · review"}
          </span>
        </div>
        <div className="px-4 py-3 grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{t("svc_tech.expected_db", "Expected (database)")}</div>
            <div className="font-mono font-bold text-slate-800">{ai.expected_sn || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{t("svc_tech.read_from_nameplate", "Read from nameplate")}</div>
            <div className={`font-mono font-bold ${snMatch ? "text-emerald-700" : "text-amber-700"}`}>{ai.extracted_sn || "(unreadable)"}</div>
          </div>
        </div>
        {!snMatch && ai.extracted_sn && (
          <div className="px-4 pb-3 text-[11px] text-amber-800 bg-amber-50 border-t border-amber-200 py-2">
            <Warning size={11} weight="fill" className="inline-block mr-1 -mt-0.5" />
            The serial number on the nameplate doesn't match the compressor record. Double-check that you photographed the right unit.
          </div>
        )}
      </div>

      {/* AI Vision · Invoice */}
      <div className="bg-white rounded-md border border-slate-200 mb-3 overflow-hidden" data-testid="svc-tech-ai-invoice">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-violet-600" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">AI Vision · Invoice</span>
          <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">
            {items.length} line{items.length === 1 ? "" : "s"} decoded
          </span>
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-4 text-xs text-slate-400">No line items decoded.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((it, i) => (
              <li key={i} className="px-4 py-2 flex items-center gap-3 text-xs">
                <span className="text-slate-300 w-5 text-right">{i + 1}.</span>
                <span className="flex-1 text-slate-700 truncate">{it.description}</span>
                <span className="text-slate-500 shrink-0">×{it.qty}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Auto-allocation */}
      {applied.length > 0 && (
        <div className="bg-white rounded-md border border-emerald-200 mb-3 overflow-hidden" data-testid="svc-tech-ai-allocations">
          <div className="px-4 py-2.5 border-b border-emerald-200 bg-emerald-50 flex items-center gap-2">
            <CheckCircle size={14} weight="fill" className="text-emerald-700" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800">Auto-allocated to SN {compressor?.serial_number}</span>
            {ai.new_quote_total != null && (
              <span className="ml-auto text-[10px] font-bold text-emerald-700">
                New quote total ${Number(ai.new_quote_total).toFixed(2)}
              </span>
            )}
          </div>
          <ul className="divide-y divide-slate-100">
            {applied.map((a, i) => (
              <li key={i} className="px-4 py-2.5 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-bold text-slate-800 truncate flex-1">{a.matched_description || a.detail_id}</span>
                  {a.split && (
                    <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-1 py-0.5 rounded-sm">Split</span>
                  )}
                </div>
                <div className="text-slate-500 flex items-center gap-2 flex-wrap">
                  <span>used <strong className="text-slate-700">{a.used_qty}</strong></span>
                  {a.split && <span>· {a.remaining_qty} remaining on quote</span>}
                  <span className="text-[10px] uppercase tracking-wider bg-violet-50 text-violet-700 border border-violet-200 px-1 py-0.5 rounded-sm ml-auto">
                    SN {a.related_serial}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Unmatched (warning) */}
      {unmatched.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3" data-testid="svc-tech-ai-unmatched">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-1 flex items-center gap-1.5">
            <Warning size={11} weight="fill" /> {unmatched.length} item{unmatched.length === 1 ? "" : "s"} couldn't be matched
          </div>
          <ul className="text-xs text-amber-900 space-y-0.5">
            {unmatched.map((u, i) => (
              <li key={i}>· {u.description} ×{u.qty} {u.reason && <span className="text-amber-700">({u.reason})</span>}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
        <button onClick={onStartOver} className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded-sm hover:bg-slate-50 inline-flex items-center justify-center gap-1" data-testid="svc-tech-start-over">
          <Plus size={12} weight="bold" /> Capture another visit
        </button>
        <button onClick={onHome} className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-blue-700 text-white rounded-sm hover:bg-blue-800" data-testid="svc-tech-go-home">
          {t("svc_tech.back_to_dashboard", "Back to dashboard")}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ───
function relTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return "—"; }
}
function prettyBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
